import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../print.css';

const PrintPersonalGapAssessment = () => {
  const { businessId } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [business, setBusiness] = useState(null);
  const [summary, setSummary] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get token from URL parameter (passed by Playwright)
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');

        if (!token) {
          throw new Error('No authentication token provided');
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };

        // Fetch business profile, personal assessment data, and all questions in parallel
        // Fetch all personal assessment types: financial, wellbeing, spiritual
        const assessmentTypes = ['personal financial', 'personal wellbeing', 'personal spiritual'].join(',');
        const [businessRes, assessmentRes, questionsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/personal-assessment/current?business_id=${businessId}`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/personal-assessment/questions?assessment_types=${encodeURIComponent(assessmentTypes)}`, { headers })
        ]);

        if (!businessRes.ok || !assessmentRes.ok) {
          throw new Error(`Failed to fetch data: ${businessRes.statusText || assessmentRes.statusText}`);
        }

        const businessData = await businessRes.json();
        const assessmentData = await assessmentRes.json();

        setBusiness(businessData.data?.profile || businessData);
        setAssessment(assessmentData);

        // Set all questions
        if (questionsRes.ok) {
          const questionsData = await questionsRes.json();
          setAllQuestions(questionsData.questions || []);
        }

        // Fetch summary if we have an assessment
        if (assessmentData.id) {
          const summaryRes = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/personal-assessment/${assessmentData.id}/summary`,
            { headers }
          );
          if (summaryRes.ok) {
            const summaryData = await summaryRes.json();
            setSummary(summaryData);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading assessment data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadData();
  }, [businessId]);

  useEffect(() => {
    if (!loading && assessment && business) {
      document.body.setAttribute('data-pdf-ready', 'true');
      document.title = `${business?.business_name || 'Business'} - Personal Gap Assessment Report`;
    }
  }, [loading, assessment, business]);

  // Helper functions
  const getScoreColor = (score) => {
    if (score > 86) return '#a07d5a';      // Bronze/tan
    if (score > 72) return '#6ba86e';      // Green
    if (score > 57) return '#6ca5d6';      // Blue
    if (score > 43) return '#d4c563';      // Yellow
    if (score > 28) return '#d9a066';      // Orange
    return '#d97373';                      // Red
  };

  const getCategoryBackgroundColor = (score) => {
    if (score > 86) return '#f5f3f0';      // Light bronze/tan
    if (score > 72) return '#f0f9f4';      // Light green
    if (score > 57) return '#f0f7fc';      // Light blue
    if (score > 43) return '#fefce8';      // Light yellow
    if (score > 28) return '#fef3e2';      // Light orange
    return '#fef2f2';                      // Light red
  };

  const getReadinessLevel = (score) => {
    if (score > 86) return 'Exceptional Readiness';
    if (score > 72) return 'Strong Readiness';
    if (score > 57) return 'Moderate Readiness';
    if (score > 43) return 'Developing Readiness';
    if (score > 28) return 'Early Stage';
    return 'Needs Significant Development';
  };

  // Calculate gap distribution
  const getGapDistribution = () => {
    if (!assessment || !assessment.responses) return [];

    // Filter out N/A responses (answer_value === 0) - same as dashboard
    const answeredQuestions = assessment.responses.filter(r => r.answer_value !== 0);

    const distribution = {
      'No Gaps': { count: 0, color: '#a07d5a' },
      'Minor Gaps': { count: 0, color: '#6ba86e' },
      'Considerable Gaps': { count: 0, color: '#6ca5d6' },
      'Critical Gaps': { count: 0, color: '#d4c563' },
      'Very Critical': { count: 0, color: '#d9a066' },
      'Extremely Critical': { count: 0, color: '#d97373' }
    };

    // Count individual question responses by their score
    answeredQuestions.forEach(response => {
      const score = response.score;
      if (score > 86) distribution['No Gaps'].count++;
      else if (score > 72) distribution['Minor Gaps'].count++;
      else if (score > 57) distribution['Considerable Gaps'].count++;
      else if (score > 43) distribution['Critical Gaps'].count++;
      else if (score > 28) distribution['Very Critical'].count++;
      else distribution['Extremely Critical'].count++;
    });

    return Object.entries(distribution).map(([name, data]) => ({
      name,
      value: data.count,
      color: data.color
    })).filter(item => item.value > 0);
  };

  // Calculate activity type scores - matches dashboard logic exactly
  const calculateActivityTypeScores = () => {
    if (!assessment || !assessment.responses || !allQuestions.length) {
      return {};
    }

    const scores = {};
    const activityTypes = ['De-Risking Activity', 'Strategy Activity', 'Efficiency Activity', 'Growth Activity', 'Culture Activity'];
    const answeredQuestions = assessment.responses; // All responses, not pre-filtered

    activityTypes.forEach(activityType => {
      // Get answered questions and join with question metadata
      const answeredTypeQuestions = answeredQuestions.filter(response => {
        // Find the matching question to get activity_type
        const question = allQuestions.find(q => q.question_id === response.question_id);
        // Match dashboard logic: check activity_type AND filter out N/A (answer_value === 0)
        return question && question.activity_type === activityType && response.answer_value !== 0;
      });

      if (answeredTypeQuestions.length > 0) {
        const totalScore = answeredTypeQuestions.reduce((sum, q) => sum + (q.score || 0), 0);
        scores[activityType] = totalScore / answeredTypeQuestions.length;
      } else {
        scores[activityType] = 0;
      }
    });

    return scores;
  };

  if (loading) {
    return (
      <div className="print-container">
        <div className="loading">Loading assessment data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="print-container">
        <div className="error">No assessment data available</div>
      </div>
    );
  }

  const gapDistributionData = getGapDistribution();
  const activityTypeScores = calculateActivityTypeScores();
  const category_scores = assessment.category_scores || {};
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="cover-header">
          <h1>Personal Gap Assessment Report</h1>
          <div className="report-type">FINANCIAL, WELLBEING, AND SPIRITUAL READINESS ANALYSIS</div>
        </div>

        <div className="cover-business-info">
          <h2>{business?.business_name || 'Business Name'}</h2>
          <p className="industry">{business?.industry || 'Industry'}</p>
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Confidential Personal Assessment Report</p>
        </div>
      </div>

      {/* Executive Summary - Page 2 */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <h1 className="section-title">Executive Summary</h1>

        {/* Overall Score and Readiness Level - Inline Layout */}
        <div style={{ display: 'flex', gap: '0.2in', marginBottom: '0.2in', flexWrap: 'wrap' }}>
          <div style={{ padding: '0.10in 0.12in', backgroundColor: 'transparent', borderRadius: '6px', borderLeft: '4px solid #1e3a8a', display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '2in' }}>
            <span style={{ fontSize: '8pt', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Overall Score:</span>
            <span style={{ fontSize: '11pt', fontWeight: '700', color: '#111827' }}>{summary?.overall_score?.toFixed(1) || assessment.overall_score?.toFixed(1)}%</span>
          </div>
          <div style={{ padding: '0.10in 0.12in', backgroundColor: 'transparent', borderRadius: '6px', borderLeft: '4px solid #3b82f6', display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '2.2in' }}>
            <span style={{ fontSize: '8pt', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Readiness Level:</span>
            <span style={{ fontSize: '11pt', fontWeight: '700', color: '#111827' }}>{summary?.readiness_level || getReadinessLevel(assessment.overall_score || 0)}</span>
          </div>
          <div style={{ padding: '0.10in 0.12in', backgroundColor: 'transparent', borderRadius: '6px', borderLeft: '4px solid #3b82f6', display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '2in' }}>
            <span style={{ fontSize: '8pt', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Questions Answered:</span>
            <span style={{ fontSize: '11pt', fontWeight: '700', color: '#111827' }}>{summary?.answered_questions || assessment.answered_questions || 0}</span>
          </div>
        </div>

        {summary && (
          <div style={{ marginBottom: '0.08in' }}>
            <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.08in', color: '#1e3a8a' }}>Assessment Overview</h3>
            <p style={{ lineHeight: '1.5', color: '#374151', fontSize: '8pt' }}>
              {summary.readiness_description}
            </p>
          </div>
        )}

        {summary?.cepa_interpretation?.executive_summary && (
          <div style={{ marginBottom: '0.08in' }}>
            <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.08in', color: '#1e3a8a' }}>Executive Summary</h3>
            <p style={{ lineHeight: '1.5', color: '#374151', fontSize: '8pt', whiteSpace: 'pre-line' }}>
              {summary.cepa_interpretation.executive_summary}
            </p>
          </div>
        )}

        {/* Scoring Guide */}
        <div style={{ marginTop: '0.2in' }}>
          <h2 className="section-title">Scoring Guide</h2>

          {/* Scoring Guide Image */}
          <div style={{ marginBottom: '0.15in', textAlign: 'center' }}>
            <img
              src="/scoring-guide.png"
              alt="Scoring Guide"
              style={{ width: '90%', maxWidth: '100%', height: 'auto', borderRadius: '0px' }}
            />
          </div>

          <div style={{ marginBottom: '0.18in' }}>
            <h3 style={{ fontSize: '9pt', fontWeight: '600', marginBottom: '0.12in', color: '#111827' }}>How Scores Are Calculated</h3>
            <p style={{ fontSize: '8pt', color: '#6b7280', lineHeight: '1.5' }}>
              Each question is scored on a scale that reflects your readiness in that area of personal and exit planning.
              The overall score is calculated by averaging all category scores across Financial, Wellbeing, and Spiritual domains,
              giving you a comprehensive view of your personal readiness for exit transition.
            </p>
          </div>

          <div>
            <h3 style={{ fontSize: '9pt', fontWeight: '600', marginBottom: '0.12in', color: '#111827' }}>Score Ranges</h3>
            <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.08in', lineHeight: '1.5' }}>
              Based on the assessment scoring scale where average performance is around 58.5%:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.06in', marginBottom: '0.12in' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#c49e73', flexShrink: 0 }}></div>
                <span style={{ fontSize: '8pt', color: '#374151' }}><strong>86-100%:</strong> Exceptional, best in class</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#a7d5a8', flexShrink: 0 }}></div>
                <span style={{ fontSize: '8pt', color: '#374151' }}><strong>72-86%:</strong> Strong, minor improvements</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#b8d4e8', flexShrink: 0 }}></div>
                <span style={{ fontSize: '8pt', color: '#374151' }}><strong>57-72%:</strong> Average, room for improvement</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#f4ebb0', flexShrink: 0 }}></div>
                <span style={{ fontSize: '8pt', color: '#374151' }}><strong>43-57%:</strong> Below average, work needed</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#f5d7b3', flexShrink: 0 }}></div>
                <span style={{ fontSize: '8pt', color: '#374151' }}><strong>28-43%:</strong> Poor, urgent attention required</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#f5c9c9', flexShrink: 0 }}></div>
                <span style={{ fontSize: '8pt', color: '#374151' }}><strong>0-28%:</strong> Critical deficiency</span>
              </div>
            </div>
            <div style={{ padding: '0.12in', backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', borderRadius: '4px' }}>
              <p style={{ fontSize: '8pt', color: '#1e3a8a', lineHeight: '1.4', margin: 0 }}>
                <strong>Note:</strong> A score of 67% is the "green zone" target representing solid preparedness. A score of
                72% or above is considered "best in class," representing clear planning, documented strategies, and
                 comprehensive readiness across all domains.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Visual Analysis - Page 3 */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <h1 className="section-title">Visual Analysis</h1>

        {/* Gap Distribution Doughnut Chart */}
        {gapDistributionData.length > 0 && (
          <div style={{ marginBottom: '0.3in' }}>
            <h2 style={{ fontSize: '11pt', fontWeight: '600', marginBottom: '0.08in', color: '#1e3a8a' }}>Gap Distribution</h2>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {/* Chart */}
              <div style={{ width: '2.8in', height: '2.8in', position: 'relative' }}>
                <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
                  {(() => {
                    const totalQuestions = gapDistributionData.reduce((sum, item) => sum + item.value, 0);
                    let currentAngle = -90;

                    return gapDistributionData.map((item, index) => {
                      const percentage = item.value / totalQuestions;
                      const angle = percentage * 360;
                      const startAngle = currentAngle;
                      const endAngle = startAngle + angle;
                      currentAngle = endAngle;

                      const largeArc = angle > 180 ? 1 : 0;

                      const baseOuterRadius = 75;
                      const baseInnerRadius = 50;

                      return (
                        <path
                          key={index}
                          d={`
                            M ${100 + baseInnerRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + baseInnerRadius * Math.sin((startAngle * Math.PI) / 180)}
                            L ${100 + baseOuterRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + baseOuterRadius * Math.sin((startAngle * Math.PI) / 180)}
                            A ${baseOuterRadius} ${baseOuterRadius} 0 ${largeArc} 1 ${100 + baseOuterRadius * Math.cos((endAngle * Math.PI) / 180)} ${100 + baseOuterRadius * Math.sin((endAngle * Math.PI) / 180)}
                            L ${100 + baseInnerRadius * Math.cos((endAngle * Math.PI) / 180)} ${100 + baseInnerRadius * Math.sin((endAngle * Math.PI) / 180)}
                            A ${baseInnerRadius} ${baseInnerRadius} 0 ${largeArc} 0 ${100 + baseInnerRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + baseInnerRadius * Math.sin((startAngle * Math.PI) / 180)}
                            Z
                          `}
                          fill={item.color}
                          style={{ filter: 'brightness(1.1)' }}
                        />
                      );
                    });
                  })()}
                  <circle cx="100" cy="100" r="50" fill="white" />
                </svg>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                  <div style={{ fontSize: '29pt', fontWeight: '700', color: '#111827' }}>
                    {Math.round(assessment.overall_score || 0)}
                  </div>
                  <div style={{ fontSize: '8pt', fontWeight: '600', color: '#6b7280' }}>Score</div>
                </div>
              </div>

              {/* Legend */}
              <div style={{ width: '3.5in', paddingLeft: '0.3in' }}>
                {gapDistributionData.map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.08in', marginBottom: '0.06in', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ width: '0.12in', height: '0.12in', borderRadius: '2px', backgroundColor: item.color, marginRight: '0.08in' }}></div>
                      <span style={{ fontSize: '10pt', color: '#374151' }}>{item.name} ({
                        item.name === 'No Gaps' ? '>86%' :
                        item.name === 'Minor Gaps' ? '72-86%' :
                        item.name === 'Considerable Gaps' ? '57-72%' :
                        item.name === 'Critical Gaps' ? '43-57%' :
                        item.name === 'Very Critical' ? '28-43%' : 
                        item.name === 'Extremely Critical' ? '0-28%' : 'N/A'
                      })</span>
                    </div>
                    <span style={{ fontSize: '10pt', fontWeight: '700', color: '#111827' }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Key Dimensions */}
        <div style={{ marginTop: '0.2in' }}>
          <h2 style={{ fontSize: '11pt', fontWeight: '600', marginBottom: '0.1in', color: '#1e3a8a' }}>Key Dimensions</h2>

          {/* Financial Dimension */}
          <div style={{ marginBottom: '0.12in', padding: '0.08in', backgroundColor: '#f0f9ff', borderRadius: '6px', borderLeft: '4px solid #3b82f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.06in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '700', color: '#1e3a8a', margin: 0 }}>Financial</h3>
              <span style={{ fontSize: '14pt', fontWeight: '700', color: getScoreColor(assessment.financial_score || 0) }}>
                {Math.round(assessment.financial_score || 0)}%
              </span>
            </div>
            <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: 0 }}>
              Questions that assess your personal financial readiness for exit, including wealth management, estate planning, tax planning,
              insurance coverage, liquidity needs, and overall financial security. These questions help ensure you have the financial foundation
              necessary for a successful transition.
            </p>
          </div>

          {/* Wellbeing Dimension */}
          <div style={{ marginBottom: '0.12in', padding: '0.08in', backgroundColor: '#f0fdf4', borderRadius: '6px', borderLeft: '4px solid #10b981' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.06in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '700', color: '#059669', margin: 0 }}>Wellbeing</h3>
              <span style={{ fontSize: '14pt', fontWeight: '700', color: getScoreColor(assessment.wellbeing_score || 0) }}>
                {Math.round(assessment.wellbeing_score || 0)}%
              </span>
            </div>
            <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: 0 }}>
              Questions that evaluate your relational and emotional preparedness for life after exit. This includes family relationships,
              marriage quality, social connections, physical health, emotional/mental health, hobbies and interests, and life purpose beyond business.
              Strong wellbeing is essential for a fulfilling post-exit life.
            </p>
          </div>

          {/* Spiritual Dimension */}
          <div style={{ marginBottom: '0.12in', padding: '0.08in', backgroundColor: '#faf5ff', borderRadius: '6px', borderLeft: '4px solid #a855f7' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.06in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '700', color: '#9333ea', margin: 0 }}>Spiritual</h3>
              <span style={{ fontSize: '14pt', fontWeight: '700', color: getScoreColor(assessment.spiritual_score || 0) }}>
                {Math.round(assessment.spiritual_score || 0)}%
              </span>
            </div>
            <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: 0 }}>
              Questions that explore your spiritual foundation, biblical wisdom application, character and integrity, eternal perspective,
              and legacy planning from a faith-based viewpoint. These questions help ensure your exit aligns with your values and creates
              lasting Kingdom impact.
            </p>
          </div>

          {/* Holistic Approach */}
          <div style={{ padding: '0.08in', backgroundColor: '#fefce8', borderRadius: '6px', borderLeft: '4px solid #eab308' }}>
            <h3 style={{ fontSize: '10pt', fontWeight: '700', color: '#854d0e', margin: '0 0 0.06in 0' }}>A Holistic Approach</h3>
            <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: 0 }}>
              True exit readiness requires attention to all three domains. Financial preparation without relational health can lead to isolation.
              Spiritual clarity without financial planning can create unnecessary stress. A balanced approach across Financial, Wellbeing, and
              Spiritual domains creates the foundation for a successful and fulfilling transition.
            </p>
          </div>
        </div>
      </div>

      {/* Personal Categories - Pages 4-5 */}
      {/* Financial Categories */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div style={{ marginBottom: '0.12in', padding: '0.12in', backgroundColor: '#ffffff', borderRadius: '6px', borderLeft: '4px solid #3b82f6' }}>
          <h2 style={{ fontSize: '17pt', fontWeight: '800', marginBottom: '0.15in', color: '#1d4ed8'}}>Financial</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15in' }}>
              {[
              { key: 'personal_financial', name: 'Personal Financial Goals & Planning' },
                { key: 'wealth_management', name: 'Wealth Management & Investment Strategy' },
                { key: 'estate_planning', name: 'Estate Planning & Document Structure' },
                { key: 'tax_planning', name: 'Tax Planning & Efficiency' },
                { key: 'insurance_risk', name: 'Insurance & Risk Protection' },
                { key: 'advisor_relationships', name: 'Advisory Team & Professional Relationships' },
                { key: 'legacy_planning', name: 'Legacy Planning & Charitable Intent' },
                { key: 'liquidity_cash', name: 'Liquidity & Cash Flow Management' },
                { key: 'family_communication', name: 'Family Financial Governance & Communication' },
                { key: 'lifestyle_resource', name: 'Post-Exit Lifestyle & Resource Alignment' },
              ].map(({ key, name }) => {
                const score = category_scores[key];
                const displayScore = score !== null && score !== undefined ? score : 0;
                const scoreColor = getScoreColor(displayScore);
                const bgColor = getCategoryBackgroundColor(displayScore);

                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '0.08in 0.12in', backgroundColor: bgColor, borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: '0.15in' }}>
                      <span style={{ fontSize: '10pt', fontWeight: '600', color: '#111827' }}>{name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '1.8in' }}>
                      <span style={{ fontSize: '10pt', fontWeight: '700', color: scoreColor, minWidth: '0.4in', textAlign: 'right' }}>
                        {score !== null && score !== undefined ? `${score.toFixed(1)}%` : 'N/A'}
                      </span>
                      <div style={{ width: '1in', height: '0.08in', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${displayScore}%`, height: '100%', backgroundColor: scoreColor, borderRadius: '4px' }}></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        </div>
      </div>

      {/* Wellbeing Categories */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div style={{ marginBottom: '0.12in', padding: '0.12in', backgroundColor: '#ffffff', borderRadius: '6px', borderLeft: '4px solid #10b981' }}>
          <h2 style={{ fontSize: '17pt', fontWeight: '800', marginBottom: '0.15in', color: '#15803d' }}>Wellbeing</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15in' }}>
              {[
                { key: 'physical_health', name: 'Physical Health & Vitality' },
                { key: 'relational_health', name: 'Relational Health & Marriage' },
                { key: 'family_relationships', name: 'Family Relationships & Legacy' },
                { key: 'emotional_mental', name: 'Emotional & Mental Wellbeing' },
                { key: 'purpose_identity', name: 'Purpose & Identity Beyond Business' },
                { key: 'time_management', name: 'Time Management & Rhythm' },
                { key: 'hobbies_interests', name: 'Hobbies, Interests & Personal Growth' },
                { key: 'social_connection', name: 'Social Connection & Community' },
                { key: 'exit_vision', name: 'Post-Exit Vision & Planning' },
                { key: 'life_transition', name: 'Life Transitions & Change Readiness' },
              ].map(({ key, name }) => {
                const score = category_scores[key];
                const displayScore = score !== null && score !== undefined ? score : 0;
                const scoreColor = getScoreColor(displayScore);
                const bgColor = getCategoryBackgroundColor(displayScore);

                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '0.08in 0.12in', backgroundColor: bgColor, borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: '0.15in' }}>
                      <span style={{ fontSize: '10pt', fontWeight: '600', color: '#111827' }}>{name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '1.8in' }}>
                      <span style={{ fontSize: '10pt', fontWeight: '700', color: scoreColor, minWidth: '0.4in', textAlign: 'right' }}>
                        {score !== null && score !== undefined ? `${score.toFixed(1)}%` : 'N/A'}
                      </span>
                      <div style={{ width: '1in', height: '0.08in', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${displayScore}%`, height: '100%', backgroundColor: scoreColor, borderRadius: '4px' }}></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        </div>
      </div>

      {/* Spiritual Categories */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div style={{ marginBottom: '0.12in', padding: '0.12in', backgroundColor: '#ffffff', borderRadius: '6px', borderLeft: '4px solid #a855f7' }}>
          <h2 style={{ fontSize: '17pt', fontWeight: '800', marginBottom: '0.15in', color: '#7e22ce' }}>Spiritual</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15in' }}>
              {[
                { key: 'personal_spiritual', name: 'Personal Spiritual Foundation' },
                { key: 'biblical_wisdom', name: 'Biblical Wisdom & Decision-Making' },
                { key: 'marriage_family', name: 'Marriage & Family Spiritual Leadership' },
                { key: 'business_stewardship', name: 'Business as Ministry & Stewardship' },
                { key: 'character_integrity', name: 'Character, Integrity & Health' },
                { key: 'eternal_impact', name: 'Eternal Impact & Crisis Readiness' },
              ].map(({ key, name }) => {
                const score = category_scores[key];
                const displayScore = score !== null && score !== undefined ? score : 0;
                const scoreColor = getScoreColor(displayScore);
                const bgColor = getCategoryBackgroundColor(displayScore);

                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '0.08in 0.12in', backgroundColor: bgColor, borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: '0.15in' }}>
                      <span style={{ fontSize: '10pt', fontWeight: '600', color: '#111827' }}>{name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '1.8in' }}>
                      <span style={{ fontSize: '10pt', fontWeight: '700', color: scoreColor, minWidth: '0.4in', textAlign: 'right' }}>
                        {score !== null && score !== undefined ? `${score.toFixed(1)}%` : 'N/A'}
                      </span>
                      <div style={{ width: '1in', height: '0.08in', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${displayScore}%`, height: '100%', backgroundColor: scoreColor, borderRadius: '4px' }}></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        </div>
      </div>

      {/* Activity Types Page - PAGE 7 */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <h1 className="section-title">Activity Type Analysis</h1>

        {/* Activity Types */}
        <div style={{ marginBottom: '0.25in' }}>
          <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.12in', lineHeight: '1.4' }}>
            Activity types categorize questions based on task completion priority and the strategic function they serve in your personal exit readiness.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.1in' }}>
            {Object.entries({
              'De-Risking Activity': 'Questions that address potential risks to your successful exit, including financial vulnerabilities, health concerns, relationship issues, and lack of planning. These activities should typically be completed first as they prevent problems that could derail your transition.',
              'Strategy Activity': 'Questions related to long-term vision, exit planning, legacy goals, and life purpose. These define the direction and meaning of your transition and should guide your other planning activities.',
              'Efficiency Activity': 'Questions about optimizing your current situation, improving systems and processes, and making better use of resources. These activities help you prepare more effectively and avoid wasting time or money.',
              'Growth Activity': 'Questions focused on developing new capabilities, expanding relationships, building spiritual depth, and increasing financial security. These activities strengthen your foundation for a successful transition.',
              'Culture Activity': 'Questions about values, character, family dynamics, and the relational environment you\'re creating. These shape the quality of your relationships and the legacy you\'ll leave behind.'
            }).map(([activityType, description]) => {
              const score = activityTypeScores[activityType] || 0;
              const color = getScoreColor(score);
              return (
                <div
                  key={activityType}
                  style={{
                    padding: '0.08in',
                    backgroundColor: color + '30',
                    border: `2px solid ${color}`,
                    borderRadius: '4px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.04in' }}>
                    <div style={{ fontSize: '8pt', fontWeight: '700', color: '#111827' }}>
                      {activityType}
                    </div>
                    <div style={{ fontSize: '11pt', fontWeight: '700', color: color }}>
                      {score.toFixed(1)}%
                    </div>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.3' }}>
                    {description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detailed Analysis Page - PAGE 8 */}
      {summary?.cepa_interpretation && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <h1 className="section-title">Detailed Analysis</h1>

          {/* Financial Readiness */}
          {summary.cepa_interpretation.financial_readiness && (
            <div style={{ marginBottom: '0.3in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in', color: '#16a34a' }}>Financial Readiness</h3>
              <p style={{ lineHeight: '1.6', color: '#6b7280', fontSize: '9pt' }}>
                {summary.cepa_interpretation.financial_readiness}
              </p>
            </div>
          )}

          {/* Wellbeing Readiness */}
          {summary.cepa_interpretation.wellbeing_readiness && (
            <div style={{ marginBottom: '0.3in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in', color: '#ea580c' }}>Wellbeing Readiness</h3>
              <p style={{ lineHeight: '1.6', color: '#6b7280', fontSize: '9pt' }}>
                {summary.cepa_interpretation.wellbeing_readiness}
              </p>
            </div>
          )}

          {/* Spiritual Readiness */}
          {summary.cepa_interpretation.spiritual_readiness && (
            <div style={{ marginBottom: '0.3in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in', color: '#9333ea' }}>Spiritual Readiness</h3>
              <p style={{ lineHeight: '1.6', color: '#6b7280', fontSize: '9pt' }}>
                {summary.cepa_interpretation.spiritual_readiness}
              </p>
            </div>
          )}

          {/* Holistic Assessment */}
          {summary.cepa_interpretation.holistic_assessment && (
            <div style={{ marginBottom: '0.3in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in', color: '#4f46e5' }}>Holistic Assessment</h3>
              <p style={{ lineHeight: '1.6', color: '#6b7280', fontSize: '9pt' }}>
                {summary.cepa_interpretation.holistic_assessment}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Strategic Recommendations Page - PAGE 9 */}
      {summary?.recommendations && summary.recommendations.length > 0 && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <h1 className="section-title">Strategic Recommendations</h1>

          <div className="next-steps">
            {summary.recommendations.map((rec, idx) => {
              // Dynamic colors based on priority level
              const priorityColors = rec.priority === 'Immediate'
                ? { background: '#fee2e2', color: '#991b1b' }  // Red
                : rec.priority === 'High'
                ? { background: '#fed7aa', color: '#9a3412' }  // Orange
                : { background: '#dbeafe', color: '#1e3a8a' }; // Blue (Medium)

              return (
                <div key={idx} className="step">
                  <h3>{rec.title}</h3>
                  <div style={{
                    display: 'inline-block',
                    padding: '0.03in 0.08in',
                    background: priorityColors.background,
                    borderRadius: '4px',
                    fontSize: '8pt',
                    fontWeight: '600',
                    marginBottom: '0.06in',
                    color: priorityColors.color
                  }}>
                    Priority: {rec.priority}
                  </div>
                  <p>{rec.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Next Steps Page - PAGE 10 */}
      {summary?.next_steps && summary.next_steps.length > 0 && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <h1 className="section-title">Next Steps</h1>

          <div className="next-steps">
            {summary.next_steps.map((step, idx) => (
              <div key={idx} className="step">
                <h3>Step {step.step}: {step.action}</h3>
                <p>{step.description}</p>
                <p style={{ fontSize: '8pt', color: '#6b7280', fontStyle: 'italic', marginTop: '0.05in' }}>
                  <strong>Timeframe:</strong> {step.timeframe}
                </p>
              </div>
            ))}
          </div>

          <div className="disclosure-box" style={{ marginTop: '0.4in' }}>
            <h3>Important Note</h3>
            <p>
              This assessment provides a comprehensive analysis of your personal exit readiness based on industry best practices
              and CEPA (Certified Exit Planning Advisor) standards. The insights are tailored to your specific situation
              and should be reviewed with your advisory team.
            </p>
            <p>
              <strong>Contact Dominion Business Advisors to discuss your personalized action plan and begin your exit planning journey.</strong>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintPersonalGapAssessment;
